import { Controller } from "@hotwired/stimulus"

// Live "what-if" recalculation for a shared broker quote page. When the broker
// changes the proximity <select>, we POST the quote's inputs to the public
// /api/v1/rate_quotes endpoint (which never persists) and update the displayed
// max LTV — and, for full quotes, the rate — without touching the saved record.
//
// The endpoint returns a "max_ltv_only" or "full_quote" payload depending on
// which inputs are supplied; ltvOnly quotes deliberately omit the pricing inputs.
export default class extends Controller {
  static targets = ["proximity", "upside", "upsideAlert", "rate", "rateRange", "message"]
  static values = {
    apiUrl: String,
    community: String,
    product: String,
    creditScore: Number,
    communityMax: Number,
    ltvOnly: Boolean,
    totalLtvPercentage: Number,
    propertyType: String,
    mortgagePosition: String
  }

  async recalc() {
    const payload = {
      community: this.communityValue,
      product: this.productValue,
      credit_score: this.creditScoreValue,
      location_type: this.proximityTarget.value
    }

    if (!this.ltvOnlyValue) {
      if (this.totalLtvPercentageValue) payload.total_ltv_percentage = this.totalLtvPercentageValue
      if (this.propertyTypeValue) payload.property_type = this.propertyTypeValue
      if (this.mortgagePositionValue) payload.mortgage_position = this.mortgagePositionValue
    }

    try {
      const response = await fetch(this.apiUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload)
      })
      if (!response.ok) return
      this.render(await response.json())
    } catch (error) {
      // Network hiccup — leave the last-known values in place.
    }
  }

  render(data) {
    // The main "maximum LTV" is the community max — fixed, never updated here.
    // Only the upside ("may consider up to X%") and its alert visibility change.
    const upside = data.broker_upside_max_ltv_percentage
    if (upside != null) {
      this.setAll("upside", `${upside}%`)
      const hasUpside = upside > this.communityMaxValue
      this.upsideAlertTargets.forEach((el) => el.classList.toggle("hidden", !hasUpside))
    }
    if (data.calculated_rate_percentage != null) {
      this.setAll("rate", `${data.calculated_rate_percentage.toFixed(2)}%`)
    }
    if (data.rate_range_low_percentage != null && data.rate_range_high_percentage != null) {
      this.setAll(
        "rateRange",
        `${data.rate_range_low_percentage.toFixed(2)}% – ${data.rate_range_high_percentage.toFixed(2)}%`
      )
    }
    if (data.message) this.setAll("message", data.message)
  }

  // Update every element carrying the given target name (a value can appear in
  // both the headline and the summary list).
  setAll(name, value) {
    this[`${name}Targets`].forEach((el) => (el.textContent = value))
  }
}
